import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { router } from '@inertiajs/vue3';
import { useConfirm } from 'primevue/useconfirm';
import {
  ADMIN_SECTIONS,
  createHttpClient,
  normalizeAccount,
  readAccountResponse,
} from './contract.js';
import { protectAdminHistory } from './historyGuard.js';
import { localAdminVisitUrl, resolveAdminContext } from './dataContext.js';
import { selectionKey, selectionTitle } from './selection.js';

export const ADMIN_WORKSPACE = Symbol('bayhive-admin-workspace');

export function useAdminWorkspace(page) {
  const confirm = useConfirm();
  const context = computed(() => resolveAdminContext(page));
  const active = computed(() => Boolean(context.value));
  const selection = computed(() => context.value?.selection || { type: 'all' });
  const contentKey = computed(() => selectionKey(selection.value));
  const panelSection = () => {
    const requested = new URL(
      page.url || window.location.href,
      window.location.origin,
    ).searchParams.get('panel');
    return ADMIN_SECTIONS.some((section) => section.key === requested) ? requested : 'details';
  };
  const state = reactive({
    account: null,
    loading: false,
    error: '',
    failedTarget: null,
    busy: 0,
    canRead: false,
    canMutate: false,
    loaded: false,
    canSearch: false,
    section: 'details',
    navigationBusy: false,
    historyBlocked: false,
  });
  const version = ref(0);
  let client = null;
  let loadSequence = 0;
  let allowVisit = false;
  let alive = true;
  let mutationTail = Promise.resolve();
  const forms = new Set();
  const pendingCommands = new Set();
  const target = computed(() => state.account?.target || context.value?.target || {});
  const can = (operation) =>
    state.loaded &&
    !state.loading &&
    !state.error &&
    state.canMutate &&
    Boolean(state.account?.capabilities?.includes(operation));
  const dirty = () => [...forms].some((form) => form.isDirty());

  function ask(header, message, acceptLabel = header) {
    return new Promise((resolve) =>
      confirm.require({
        group: 'shop-management-actions',
        header,
        message,
        acceptLabel,
        rejectLabel: 'Cancel',
        defaultFocus: 'reject',
        acceptProps: { size: 'small' },
        rejectProps: { size: 'small', severity: 'secondary', outlined: true },
        accept: () => resolve(true),
        reject: () => resolve(false),
        onHide: () => resolve(false),
      }),
    );
  }

  async function settle() {
    await mutationTail;
    await Promise.allSettled([...pendingCommands]);
    await Promise.allSettled([...forms].map((form) => form.wait?.()));
    for (const form of [...forms]) {
      if (form.isDirty() && form.autosave) await form.flush();
    }
    if (state.busy) return false;
    if (!dirty()) return true;
    if (
      !(await ask(
        'Discard changes',
        'Some changes have not been saved. Discard them and continue?',
      ))
    )
      return false;
    for (const form of [...forms]) form.discard();
    return true;
  }

  async function load(id) {
    if (!state.canRead) return;
    const sequence = ++loadSequence;
    state.loading = true;
    state.error = '';
    state.failedTarget = id;
    try {
      const account = readAccountResponse(await client.load(id), id);
      if (!alive || sequence !== loadSequence) return;
      if (Number(account.target.id) !== Number(id))
        throw new Error('The account response does not match the selected shop.');
      state.account = account;
      state.loaded = true;
      state.section = panelSection();
      state.failedTarget = null;
      version.value++;
    } catch (error) {
      if (alive && sequence === loadSequence)
        state.error =
          error.response?.data?.message || error.message || 'The account could not be loaded.';
    } finally {
      if (alive && sequence === loadSequence) state.loading = false;
    }
  }

  async function switchSelection(next, address = page.url) {
    if (
      !['all', 'shop', 'mso'].includes(next?.type) ||
      state.loading ||
      state.navigationBusy ||
      historyGuard.isBlocked()
    )
      return;
    if (next.type !== 'all' && (!Number.isSafeInteger(Number(next.id)) || Number(next.id) < 1))
      return;
    state.navigationBusy = true;
    try {
      if (!(await settle()) || !(await historyGuard.whenRestored())) return;
      allowVisit = true;
      const visit = new Promise((resolve) =>
        router.visit(localAdminVisitUrl(page, address, next), {
          preserveState: false,
          onFinish: resolve,
          onCancel: resolve,
        }),
      );
      allowVisit = false;
      await visit;
    } finally {
      allowVisit = false;
      state.navigationBusy = false;
    }
  }

  function switchShop(shop, destination = null) {
    if (!shop?.id) return;
    const parameters = {
      shop_id: context.value.originShopId,
      target_shop: shop.id,
      ...(selection.value.type === 'mso' ? { admin_return_mso: selection.value.id } : {}),
      ...(destination || {}),
    };
    return switchSelection(
      { ...shop, type: 'shop' },
      route(destination ? 'bayhive-admin.workspace' : 'bayhive-admin.show', parameters),
    );
  }

  async function mutate(operation, changes, targetId = target.value.id) {
    if (Number(targetId) !== Number(target.value.id) || !can(operation))
      throw new Error('This operation is not available for this account.');
    state.busy++;
    // Full account responses must be applied in request order; concurrent
    // section autosaves must not restore an older snapshot over a newer save.
    const request = mutationTail.then(async () => {
      if (Number(targetId) !== Number(target.value.id) || !can(operation))
        throw new Error('This operation is no longer available for this account.');
      const result = await client.mutate(targetId, operation, changes);
      if (result.status && result.status !== 'completed')
        throw new Error(
          'The change has not been confirmed. Reload the account to check its status before retrying.',
        );
      if (!result.account || Number(result.account.target?.id) !== Number(targetId))
        throw new Error(
          'The saved account response could not be confirmed. Reload before trying again.',
        );
      const account = readAccountResponse(result.account, targetId);
      if (
        (operation === 'features' &&
          Object.entries(changes).some(
            ([key, value]) =>
              !Object.hasOwn(account.features || {}, key) || account.features[key] !== value,
          )) ||
        (operation === 'kiosk-access' && account.kiosk?.enabled !== changes.enabled)
      )
        throw new Error(
          'The saved settings could not be confirmed. Reload the account before retrying.',
        );
      if (alive && Number(targetId) === Number(target.value.id)) state.account = account;
      return result;
    });
    mutationTail = request.catch(() => {});
    try {
      return await request;
    } finally {
      state.busy--;
    }
  }

  const stop = watch(
    context,
    async (value) => {
      const sequence = ++loadSequence;
      state.error = '';
      state.failedTarget = null;
      state.section = panelSection();
      state.canRead = false;
      state.canMutate = false;
      state.loaded = false;
      state.canSearch = false;
      state.loading = false;
      if (!value) {
        state.account = null;
        return;
      }
      state.account = value.target ? normalizeAccount(value) : null;
      client = createHttpClient(value.endpoints);
      if (!alive || sequence !== loadSequence) return;
      const controlPanel =
        value.selection?.type === 'shop' &&
        value.target?.id &&
        value.scope === 'account' &&
        value.workspace === 'accounts' &&
        value.section === 'control-panel';
      state.canRead = controlPanel && client.canRead;
      state.canMutate = controlPanel && client.canMutate;
      state.canSearch = client.canSearch;
      if (state.canRead) await load(value.target.id);
    },
    { immediate: true },
  );

  const historyGuard = protectAdminHistory({
    active: () => active.value,
    shouldGuard: () => dirty() || state.busy || pendingCommands.size,
    settle,
    busy: () => state.navigationBusy,
    setBusy: (value) => {
      state.navigationBusy = value;
    },
    onBlocked: (value) => {
      state.historyBlocked = value;
    },
  });
  const removeBefore = router.on('before', (event) => {
    event.detail.visit.url = localAdminVisitUrl(page, event.detail.visit.url);
    if (historyGuard.isBlocked()) {
      event.preventDefault();
      return;
    }
    if (!active.value || allowVisit || (!dirty() && !state.busy && !pendingCommands.size)) return;
    event.preventDefault();
    if (state.navigationBusy) return;
    state.navigationBusy = true;
    const visit = event.detail.visit;
    settle()
      .then(async (ok) => {
        if (!ok || !(await historyGuard.whenRestored())) return;
        allowVisit = true;
        router.visit(visit.url, {
          method: visit.method,
          data: visit.data,
          replace: visit.replace,
          preserveScroll: visit.preserveScroll,
          preserveState: visit.preserveState,
        });
        allowVisit = false;
      })
      .finally(() => {
        state.navigationBusy = false;
      });
  });
  const beforeUnload = (event) => {
    if (active.value && (dirty() || state.busy || pendingCommands.size)) {
      event.preventDefault();
      event.returnValue = '';
    }
  };
  window.addEventListener('beforeunload', beforeUnload);
  onBeforeUnmount(() => {
    alive = false;
    loadSequence++;
    stop();
    removeBefore();
    historyGuard.stop();
    window.removeEventListener('beforeunload', beforeUnload);
  });

  return {
    active,
    context,
    selection,
    contentKey,
    title: computed(() => selectionTitle(selection.value)),
    href: (address) => localAdminVisitUrl(page, address),
    state,
    target,
    version,
    can,
    ask,
    settle,
    continueHistoryNavigation: historyGuard.continueNavigation,
    switchShop,
    switchSelection,
    returnToMso: () =>
      context.value.returnMsoId && switchSelection({ type: 'mso', id: context.value.returnMsoId }),
    mutate,
    track(promise) {
      pendingCommands.add(promise);
      promise.finally(() => pendingCommands.delete(promise)).catch(() => {});
      return promise;
    },
    selectPanel(section) {
      if (state.navigationBusy || historyGuard.isBlocked()) return;
      const url = new URL(window.location.href);
      url.searchParams.set('panel', section);
      router.visit(url.toString(), { preserveScroll: true, preserveState: true });
    },
    register(form) {
      forms.add(form);
      return () => forms.delete(form);
    },
    search: (q) => client.search(q),
    retry: () => load(state.failedTarget || target.value.id),
  };
}
